import type { FC } from 'react';
import React from 'react';
import cn from 'classnames';
import { useForm } from 'react-final-form';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { Plate } from '@alfalab/core-components-plate';
import { Badge } from '@alfalab/core-components-badge';
import { AlertCircleMIcon } from '@alfalab/icons-glyph/AlertCircleMIcon';

import { INN_TRUE, INN_FALSE } from '../../config';
import { useFormContext } from '../form/context';
import type { TDaDataValuesWithEvent } from '../../helpers/models';
import type { TInnStatuses } from '../../models';

import type { TCompanyFields } from './models';

const innFieldName = 'inn';
const locationFieldName = 'location';
export const CompanyFields: FC<TCompanyFields> = ({
    Aligner,
    AutocompleteField,
    FormInput,
    Link,
    Select,
    Tag,
    Text,
    linkToCheckInn,
    locationPlaceholder,
    rowStyle,
    styles,
}) => {
    const { fields, formId, innStatus, setInnStatus } = useFormContext();

    const form = useForm();

    const handleSelect = ({ data }: TDaDataValuesWithEvent) => {
        if (data) {
            form.change(locationFieldName, data.address.data.city);
            form.change(innFieldName, data.inn);
        }
    };

    const handleTagClick = (status: TInnStatuses) => {
        sendEvent({
            event: 'site_formRequestField',
            name: 'Ввод ИНН',
            content: status === 'inn_true' ? 'Ввести ИНН' : 'Не вводить',
            form_id: formId,
        });
        setInnStatus(status);
    };

    return (
        <>
            <Text
                className={cn(styles.external, styles.text)}
                fontSize={16}
                fontWeight={4}
                kind="caption"
                lineHeight={24}
                styler={{ marginTop: 24, marginBottom: 12 }}
                tag="p"
            >
                Сделаем предложение быстрее, если введёте ИНН
            </Text>

            <Tag
                label="Ввести ИНН"
                onClick={handleTagClick}
                selected={innStatus}
                size="xs"
                value={INN_TRUE}
            ></Tag>
            <Tag
                label="Не вводить"
                onClick={handleTagClick}
                selected={innStatus}
                size="xs"
                value={INN_FALSE}
            ></Tag>

            {innStatus === INN_TRUE && (
                <>
                    <Aligner align="left" className={rowStyle}>
                        <AutocompleteField onSelect={handleSelect} strict type="inn">
                            <Select name="inn" placeholder="ИНН" />
                        </AutocompleteField>
                    </Aligner>
                    <Aligner align="left" styler={{ marginTop: 4, marginBottom: 0 }}>
                        <Link href={linkToCheckInn} target="_blank">
                            <Text
                                fontSize={14}
                                fontWeight={4}
                                kind="caption"
                                lineHeight={18}
                                tag="span"
                            >
                                Не знаете свой ИНН?
                            </Text>
                        </Link>
                    </Aligner>
                </>
            )}
            {innStatus === INN_FALSE && (
                <Plate
                    className={cn(styles.external, styles.plate)}
                    leftAddons={
                        <Badge content={<AlertCircleMIcon />} iconColor="attention" view="icon" />
                    }
                    title="Работаем только с юридическими лицами и ИП"
                    titleView="light"
                    view="attention"
                />
            )}
            {fields?.includes('location') && (
                <FormInput
                    className={rowStyle}
                    isRequired
                    name={locationFieldName}
                    settings={{
                        caption: 'Подберём менеджера в вашем регионе',
                        placeholder: locationPlaceholder,
                        maxLength: 200,
                        accept: '^[а-яА-яёЁ\\d\\s.,;:\\-\\/№]*$',
                    }}
                />
            )}
        </>
    );
};
